/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Stack,
    useStyleConfig,
    Box,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import useEmailSignupFields from '../../../../forms/useEmailSignupFields'
import Field from '../../../../field'

const EmailSignUpForm = ({
    bodyColor,
    isCenterAligned,
    enableBirthdaySelection,
    buttonText,
    buttonTextColor,
    buttonBgColor,
    onSuccess
}) => {
    const form = useForm()
    const fields = useEmailSignupFields({form})
    const styles = useStyleConfig('FormStyles')
    const [showLoadingCTA, setShowLoadingCTA] = useState()

    const hasButton = buttonText

    // extend styles
    styles.fieldsStack = {
        ...styles.fieldsStack,
        direction: {base: 'column', md: isCenterAligned ? 'row' : 'column'},
        color: bodyColor || 'black.100'
    }

    const submitEmailSignup = (data) => {
        const {email, birthdayMonth} = data
        window._ltk.Signup.New(
            'BirthdaySignup',
            'emailSignupForm',
            window._ltk.Signup.TYPE.CUSTOM,
            'btnSubmitEmailSignup'
        )
        window._ltk.Signup.SetValue('BirthdaySignup', 'ltkEmail', email)
        window._ltk.Signup.SetValue('BirthdaySignup', 'ltkBdayMonth', birthdayMonth)
        window._ltk.Signup.SetValue('BirthdaySignup', 'ltkSaved', true)
        window._ltk.Signup.Subscribe('BirthdaySignup')

        setShowLoadingCTA(true)
        setTimeout(() => {
            setShowLoadingCTA(false)
            onSuccess?.()
        }, 500)
    }

    return (
        <Box>
            <form
                id="emailSignupForm"
                name="email-signup-form"
                onSubmit={form.handleSubmit(submitEmailSignup)}
                data-testid="sf-email-signup-form"
            >
                <Stack {...styles.fieldsStack}>
                    <Field
                        {...fields.email}
                        inputId="emailSignupField"
                        inputName="email_signup_field"
                        backgroundColor="white"
                    />
                    {enableBirthdaySelection === 'yes' && (
                        <Field
                            {...fields.birthDayMonth}
                            inputId="birthdayMonthSignUpField"
                            inputName="birthdayMonth_signup_field"
                            backgroundColor="white"
                        />
                    )}
                </Stack>

                {hasButton && (
                    <Box marginTop={{base: '40px', lg: isCenterAligned ? '40px' : '20px'}}>
                        <Button
                            id="btnSubmitEmailSignup"
                            type="submit"
                            variant="button"
                            background={buttonBgColor}
                            color={buttonTextColor}
                            _hover={{background: buttonBgColor}}
                            fontFamily="inter"
                            isLoading={form.formState.isSubmitting || showLoadingCTA}
                            fontWeight="400"
                            sx={{
                                _hover: {
                                    backgroundColor: `color-mix(in srgb, ${buttonBgColor} 88%, white 12%)`
                                }
                            }}
                            paddingX="50px"
                        >
                            {buttonText}
                        </Button>
                    </Box>
                )}
            </form>
        </Box>
    )
}

EmailSignUpForm.propTypes = {
    isCenterAligned: PropTypes.bool,
    enableBirthdaySelection: PropTypes.string,
    bodyColor: PropTypes.string,
    buttonText: PropTypes.string,
    buttonTextColor: PropTypes.string,
    buttonBgColor: PropTypes.string,
    onSuccess: PropTypes.func
}

export default EmailSignUpForm
